import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { IMAGE_URL_PREFIX } from '../../constants';

require('./DoctorCard.scss');

const staticText = {
  specialist: 'SPECIALIST',
  drPrefix: 'Dr. ',
  yearsExp: '+ years exp.',
  speaks: 'Speaks: ',
  select: 'Select',
};

class DoctorCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visibleTags: [],
      remainingCount: 0,
      firstTagNeedsEllipsis: false,
      firstTagMaxWidth: null,
    };
    this.wrapperRef = null;
    this.tagRefs = [];
    this.observer = null;
  }

  componentDidMount() {
    this.calculateTags();
    if (typeof ResizeObserver !== 'undefined' && this.wrapperRef) {
      this.observer = new ResizeObserver(() => this.calculateTags());
      this.observer.observe(this.wrapperRef);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.doctor !== this.props.doctor) {
      this.calculateTags();
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  calculateTags() {
    const { doctor } = this.props;
    if (!doctor.conditionsTreated || !doctor.conditionsTreated.length || !this.wrapperRef) return;

    const allExpertise = doctor.conditionsTreated.map(s => s.trim());
    const GAP_WIDTH = 8;
    const MORE_TAG_WIDTH = 75;
    const wrapperWidth = this.wrapperRef.offsetWidth;

    let usedWidth = 0;
    const allVisibleTags = [];
    let needsEllipsis = false;
    let maxWidth = null;

    const hasMultipleItems = allExpertise.length > 1;
    const maxWidthForChips = hasMultipleItems
      ? wrapperWidth - MORE_TAG_WIDTH - GAP_WIDTH
      : wrapperWidth;

    for (let idx = 0; idx < allExpertise.length; idx++) {
      const tagWidth = this.tagRefs[idx] ? this.tagRefs[idx].offsetWidth : 0;
      const gapWidth = allVisibleTags.length > 0 ? GAP_WIDTH : 0;
      const totalNeeded = usedWidth + gapWidth + tagWidth;

      if (totalNeeded <= maxWidthForChips) {
        allVisibleTags.push(allExpertise[idx]);
        usedWidth = totalNeeded;
      } else {
        if (allVisibleTags.length === 0) {
          allVisibleTags.push(allExpertise[idx]);
          needsEllipsis = true;
          maxWidth = maxWidthForChips;
        }
        break;
      }
    }

    this.setState({
      visibleTags: allVisibleTags,
      firstTagNeedsEllipsis: needsEllipsis,
      firstTagMaxWidth: maxWidth,
      remainingCount: allExpertise.length - allVisibleTags.length,
    });
  }

  render() {
    const { doctor, selectedDoctorId, onSelect } = this.props;
    const { visibleTags, remainingCount, firstTagNeedsEllipsis, firstTagMaxWidth } = this.state;

    const isSelected = selectedDoctorId === doctor.id;
    const allExpertise = (doctor.conditionsTreated || []).map(s => s.trim());
    const languages = (doctor.language || []).join(', ');
    const isRatingAvailable = !!(doctor.rating && doctor.rating.reviewsText);
    const hasConsultations = !!doctor.noOfConsultationsText;
    const showConsultationInfo = isRatingAvailable || hasConsultations;
    const showDiscount = doctor.discountPercentage > 0;
    const isSpecialist = doctor.doctorType === 'SPECIALIST';

    return (
      <div
        className={'doctorCardNew' + (isSelected ? ' doctorCardNewSelected' : '')}
        onClick={() => onSelect(doctor)}
      >
        <div className="doctorCardNewInner">

          {/* Upper: photo + info */}
          <div className="doctorCardUpperContent">
            <div className="doctorProfileCol">
              {doctor.profileUrl ? (
                <img
                  src={doctor.profileUrl}
                  alt={doctor.firstName}
                  className={'doctorProfileImg' + (isSpecialist ? ' specialistImg' : '')}
                />
              ) : (
                <div className="doctorProfilePlaceholder">
                  <span>{(doctor.firstName || 'D')[0]}</span>
                </div>
              )}
              {isSpecialist && (
                <span className="specialistTag">{staticText.specialist}</span>
              )}
            </div>

            <div className="doctorRightSide">
              <div className="doctorInfoBlock">
                <h3 className="doctorCardName">{staticText.drPrefix}{doctor.firstName} {doctor.lastName}</h3>
                {doctor.specialization && (
                  <p className="doctorCardSpecialization">{doctor.specialization}</p>
                )}
                <p className="doctorCardExperience">
                  {doctor.yearsOfExperience}{staticText.yearsExp}{doctor.degrees ? <span> • {doctor.degrees}</span> : null}
                </p>

                {/* Expertise chips */}
                {allExpertise.length > 0 && (
                  <div
                    className="expertiseInWrapper"
                    ref={(el) => { this.wrapperRef = el; }}
                  >
                    <div className="hiddenMeasureBlock">
                      {allExpertise.map((item, i) => (
                        <span
                          key={i}
                          ref={(el) => { this.tagRefs[i] = el; }}
                          className="expertiseTag"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                    {visibleTags.map((item, index) => (
                      <span
                        key={index}
                        className={'expertiseTag' + (index === 0 && firstTagNeedsEllipsis ? ' expertiseTagEllipsis' : '')}
                        style={index === 0 && firstTagNeedsEllipsis && firstTagMaxWidth ? { maxWidth: firstTagMaxWidth + 'px' } : {}}
                      >
                        {item}
                      </span>
                    ))}
                    {remainingCount > 0 && (
                      <span className="expertiseTag moreTag">+{remainingCount} more</span>
                    )}
                  </div>
                )}

                {/* Language */}
                {languages && (
                  <div className="doctorLanguageWrapper">
                    <span className="doctorLanguageText">{staticText.speaks}{languages}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Consultation info: rating + consultations */}
          {showConsultationInfo && (
            <div className="gradientBorder">
              <div className="consultationInfoWrapper">
                {isRatingAvailable && (
                  <div className="consultationInfo">
                    <img
                      src={`${IMAGE_URL_PREFIX}doc-consult/thumb.png`}
                      alt="rating"
                      className="consultationIcon"
                    />
                    <span className="consultationText">{doctor.rating.reviewsText}</span>
                  </div>
                )}
                {hasConsultations && (
                  <div className="consultationInfo">
                    <img
                      src={`${IMAGE_URL_PREFIX}doc-consult/reviews.png`}
                      alt="consultations"
                      className="consultationIcon"
                    />
                    <span className="consultationText">{doctor.noOfConsultationsText}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer: price + select slot + availability */}
          <div className="doctorCardNewFooter">
            <div className="doctorPriceCol">
              {showDiscount && (
                <div className="priceRow">
                  <span className="strikeThroughPrice">₹{doctor.consultationFees}</span>
                  <span className="discountBadge">{doctor.discountPercentage}%</span>
                </div>
              )}
              <p className="discountedPrice">₹{doctor.discountedFees}</p>
            </div>

            <div className="doctorFooterRight">
              <button
                className={`selectSlotBtn ${isSelected ? ' doctorCardNewSelected' : ''}`}
                onClick={(e) => { e.stopPropagation(); onSelect(doctor); }}
              >
                {staticText.select}
              </button>
              {doctor.availabilityMessage && (
                <div className="availableInfo">
                  {doctor.isAvailableSoon ? (
                    <div className="availableRow">
                      <img
                        src={`${IMAGE_URL_PREFIX}icon_thunderIcon.svg`}
                        alt="available"
                        className="thunderIcon"
                      />
                      <span className="doctorAvailable">{doctor.availabilityMessage}</span>
                    </div>
                  ) : (
                    <span className="doctorNotAvailable">{doctor.availabilityMessage}</span>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    );
  }
}

DoctorCard.propTypes = {
  doctor: PropTypes.object.isRequired,
  selectedDoctorId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

DoctorCard.defaultProps = {
  selectedDoctorId: null,
};

export default DoctorCard;
